import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

import { useLocation } from 'react-router-dom';

import CreateInvoiceV2 from './components/CreateInvoiceV2/CreateInvoiceV2';
import { UserDetailsContext } from 'context/userDetailsContext';
import { handlePageNumberInPagination } from 'common/pagination';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { tableCell } from 'modules/reports/components/TableCell/TableCell';
import IntuitPaymentModal from 'modules/client/intuit/IntuitPaymentModal';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import InvoicesFiltersV2 from './components/InvoicesFiltersV2/InvoicesFiltersV2';
import { getPageLimit, getFilePathForPagination, pickExtractedDate } from './helpers/invoicesHelpersV2';
import {
  caseBody as caseBodyHelper,
  viewBody as viewBodyHelper,
  totalFeeBody as totalFeeBodyHelper,
  statusBody as statusBodyHelper,
  dueBody as dueBodyHelper,
  dueTodayBody as dueTodayBodyHelper,
  nextDueDateBody as nextDueDateBodyHelper,
  dueDateBody as dueDateBodyHelper,
  daysPastDueBody as daysPastDueBodyHelper,
  actionBody as actionBodyHelper,
} from './helpers/invoicesDataTableHelpersV2';
import { useInvoiceListV2Query } from './hooks/queries/useInvoiceListV2Query';
import { useQueryClient } from '@tanstack/react-query';
import { RecordPaymentV2 } from '../components/RecordPaymentV2/RecordPaymentV2.jsx';
import { SendNotificationMailV2 } from '../components/SendNotificationMailV2/SendNotificationMailV2.jsx';
import { RemindersV2 } from '../components/RemindersV2/RemindersV2';
import { queryKeys } from '../config/queryClient';
import { useDeleteInvoiceQuery } from './hooks/mutations/useDeleteInvoiceQuery';

const InvoiceV2 = (props) => {
  const {
    isFromCaseSpecificModule,
    getCaseDetails,
    getTimeEntrySummarty,
    isFromGlobalBilling,
    onTrustHorizonRefresh,
    isActiveTab = true,
  } = props || {};
  const userContext = useContext(UserDetailsContext);
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();

  // State management
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState();
  const [viewInvoice, setViewInvoice] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [confirmSendMail, setConfirmSendMail] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [filters, setFilters] = useState({
    searchKey: '',
    selectedDate: '',
    selectedPractice: null,
    selectedLeadAttorneyList: null,
  });
  const [pageLimit, setPageLimit] = useState(getPageLimit(props.isLeadSpecific, isFromCaseSpecificModule, userContext) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null); // Default sort by sent_date
  const [sortOrder, setSortOrder] = useState(null); // 1 for ascending, -1 for descending

  // Map DataTable field names to API sort_by field names
  // API supports: invoice_number, case_name, viewed, total_amount, invoice_status, due_amount, due_today, next_due_date, sent_date, days_past_due
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return ''; // Return empty string when no sort field selected
    const fieldMap = {
      invoice_number: 'invoice_number',
      'case_description.case_name': 'case_name',
      case_name: 'case_name',
      viewed_status: 'viewed',
      viewed: 'viewed',
      total_flat_fee: 'total_amount', // Original uses total_flat_fee field but API uses total_amount
      total_amount: 'total_amount',
      invoice_status: 'invoice_status',
      due_amount: 'due_amount',
      plan_due_amount: 'due_today', // Due Today column field maps to API due_today
      due_today: 'due_today',
      next_due_date: 'next_due_date',
      invoice_date: 'sent_date', // Original uses invoice_date field but API uses sent_date
      sent_date: 'sent_date',
      due_date: 'days_past_due', // Original uses due_date field but API uses days_past_due
      days_past_due: 'days_past_due',
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // Return empty string when no sort order selected
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  const queryFilters = useMemo(() => {
    const baseFilters = {
      case_id: props.caseView ? props?.caseDetails?.case_id : '',
      invoice_status: '',
      practice_area_list: filters.selectedPractice || [],
      invoice_date_lower: '',
      invoice_date_upper: '',
      sort_by: getCurrentSortField,
      sort_order: getCurrentSortOrder,
      search_key: filters.searchKey || '',
      lead_attorney_list: filters.selectedLeadAttorneyList || [],
      ...pickExtractedDate(filters.selectedDate, userContext?.userDetails?.timezone),
    };
    return baseFilters;
  }, [props.caseView, props?.caseDetails, filters, userContext, getCurrentSortField, getCurrentSortOrder]);

  // Use React Query hook for RDS-based pagination
  const {
    invoices,
    totalRecords,
    currentPage: apiCurrentPage,
    isLoading,
    refetch,
  } = useInvoiceListV2Query({
    limit: pageLimit,
    page: currentPage,
    invoice_status: queryFilters.invoice_status,
    practice_area_list: queryFilters.practice_area_list,
    case_id: queryFilters.case_id,
    invoice_date_lower: queryFilters.invoice_date_lower,
    invoice_date_upper: queryFilters.invoice_date_upper,
    sort_by: queryFilters.sort_by,
    sort_order: queryFilters.sort_order,
    search_key: queryFilters.search_key,
    lead_attorney_list: queryFilters.lead_attorney_list,
    enabled: isActiveTab, // Only fetch when tab is active
  });

  // Delete invoice mutation
  const deleteInvoice = useDeleteInvoiceQuery({
    onSuccess: () => {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DELETE_INVOICE_SUCCESS);
      refetch();
      typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
      if (isFromCaseSpecificModule) {
        onTrustHorizonRefresh();
      }
    },
    onError: (err) => {
      let msg = toastConstant.message.DELETE_INVOICE_FAILED;
      if (err && err?.response?.data?.message) {
        msg = err.response.data.message;
      }
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, msg);
    },
  });

  // Table body renderers using helpers
  const caseBody = (rowData) => caseBodyHelper({ rowData });
  const viewBody = (rowData) => viewBodyHelper({ rowData, timezone: userContext?.userDetails?.timezone });
  const totalFeeBody = (rowData) => totalFeeBodyHelper({ rowData });
  const statusBody = (rowData) => statusBodyHelper({ rowData });
  const dueBody = (rowData) => dueBodyHelper({ rowData });
  const dueTodayBody = (rowData) => dueTodayBodyHelper({ rowData });
  const nextDueDateBody = (rowData) => nextDueDateBodyHelper({ rowData, timezone: userContext?.userDetails?.timezone });
  const dueDateBody = (rowData) => dueDateBodyHelper({ rowData, timezone: userContext?.userDetails?.timezone });
  const daysPastDueBody = (rowData) => daysPastDueBodyHelper({ rowData, timezone: userContext?.userDetails?.timezone });
  const actionBody = (rowData) =>
    actionBodyHelper({
      rowData,
      onView: (rowData) => {
        setSelectedInvoice({ ...rowData });
        setViewInvoice(true);
        setShowCreateInvoice(true);
      },
      onSendMail: (rowData) => {
        setSelectedInvoice({ ...rowData });
        setConfirmSendMail(true);
      },
      onRecordPayment: (rowData) => {
        setSelectedInvoice({ ...rowData });
        setShowRecordModal(true);
      },
      onAcceptPayment: (rowData) => {
        setShowPaymentModal(rowData);
      },
      onReminder: (rowData) => {
        setSelectedInvoice({ ...rowData });
        setShowReminder(true);
      },
      onDelete: (rowData) => {
        setSelectedInvoice({ ...rowData });
        setConfirmDelete(true);
      },
      userContext,
    });

  // Handlers
  const onSuccess = () => {
    setShowCreateInvoice(false);
    setShowRecordModal(false);
    if (props?.setShowPaymentPlanCreateInvoice && props?.setSelectedPaymentPlan) {
      props?.setShowPaymentPlanCreateInvoice(false);
      props?.setSelectedPaymentPlan({});
    }
    // Invalidate and refetch invoices, time entries, expenses, trust accounting, operating accounting, and temporary funds
    queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.trustAccounting.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.operatingAccounting.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.temporaryFunds.all });
    refetch();
    typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
    if (
      (props?.caseDetails && !props?.caseDetails?.default_trust_account?.[0]?.account_id) ||
      !props?.caseDetails?.default_operating_account?.[0]?.account_id
    ) {
      typeof getCaseDetails === 'function' && getCaseDetails();
    }
    if (isFromCaseSpecificModule) {
      setTimeout(() => {
        onTrustHorizonRefresh();
      }, 3000);
    }
  };

  const onCreateSuccess = () => {
    setShowReminder(false);
    // Invalidate and refetch invoices
    queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    refetch();
    typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
  };

  const onDeleteInvoice = () => {
    if (!selectedInvoice?.sk) return;

    deleteInvoice.mutate({
      invoice_sk: selectedInvoice.sk,
      is_lead: props?.isLeadSpecific ? props?.isLeadSpecific : selectedInvoice?.is_lead,
    });
  };

  // Handle filter changes from InvoicesFiltersV2
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination handlers for RDS-based pagination
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    let path = getFilePathForPagination(props.isLeadSpecific, isFromCaseSpecificModule);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
  };

  // Sorting handler
  const onSort = (event) => {
    const { sortField: field, sortOrder: order } = event;

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  useEffect(() => {
    if (localStorage.getItem('redirectToInvoice')) {
      setShowCreateInvoice(true);
      localStorage.removeItem('redirectToInvoice');
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invoiceSk = queryParams.get('invoice_sk');

    if (invoiceSk && !selectedInvoice) {
      const decodedInvoiceSk = decodeURIComponent(invoiceSk);
      const invoiceData = { sk: decodedInvoiceSk };
      setSelectedInvoice(invoiceData);
      setViewInvoice(true);
      setShowCreateInvoice(true);
    } else if (!invoiceSk && !selectedInvoice) {
      let invoiceDetails = location?.state?.invoiceDetails || getLocalStorageParseItem('stateData')?.invoiceDetails;

      if (invoiceDetails && !selectedInvoice) {
        setSelectedInvoice(invoiceDetails);
        setViewInvoice(true);
        setShowCreateInvoice(true);
        removeLocalStorageItem('stateData');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (showCreateInvoice) {
      document.body.style.overflow = 'hidden';
    }
    // Clean up the effect when the component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showCreateInvoice]); // to remove the dropdown scroll issue in dialog dropdown

  return (
    <div>
      {showRecordModal && (
        <RecordPaymentV2
          show={showRecordModal}
          onHide={() => {
            setShowRecordModal(false);
          }}
          selectedInvoice={selectedInvoice}
          onSuccess={onSuccess}
          caseDetails={props?.caseDetails}
        />
      )}

      {showCreateInvoice && (
        <CreateInvoiceV2
          show
          onHide={() => {
            setShowCreateInvoice(false);
            const queryParams = new URLSearchParams(location.search);
            const hadInvoiceSk = queryParams.has('invoice_sk');

            if (hadInvoiceSk) {
              const cleanUrl = window.location.href.split('?')[0];
              window.history.replaceState({}, '', cleanUrl);
            }

            if (props?.setShowPaymentPlanCreateInvoice && props?.setSelectedPaymentPlan) {
              props?.setShowPaymentPlanCreateInvoice(false);
              props?.setSelectedPaymentPlan({});
            }
          }}
          onSuccess={onSuccess}
          selectedInvoice={selectedInvoice}
          viewInvoice={viewInvoice}
          caseView={props.caseView}
          isLeadSpecific={props.isLeadSpecific}
          caseDetails={props.caseDetails}
          setShowRecordModal={setShowRecordModal}
          loadInvoices={refetch}
          filters={queryFilters}
          getPageLimit={() => getPageLimit(props.isLeadSpecific, isFromCaseSpecificModule, userContext)}
          updateCaseDetails={props?.updateCaseDetails}
          getCaseDetails={getCaseDetails}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          className="common-popup-style confirm-delete-popup"
          visible={confirmDelete}
          rejectClassName="p-button-secondary outline"
          onHide={() => setConfirmDelete(false)}
          header="Confirm Delete"
          message={' Do you want to delete this invoice?'}
          icon="pi pi-exclamation-triangle"
          accept={() => {
            onDeleteInvoice();
          }}
          reject={() => setConfirmDelete(false)}
        />
      )}
      {confirmSendMail && (
        <SendNotificationMailV2
          show={confirmSendMail}
          onHide={() => {
            setConfirmSendMail(false);
          }}
          selectedRequest={selectedInvoice}
          invoiceView={true}
          isLeadSpecific={props.isLeadSpecific}
        />
      )}

      {showReminder && (
        <RemindersV2
          show
          onHide={() => {
            setShowReminder(false);
          }}
          selectedRequest={{ ...selectedInvoice, ...(selectedInvoice?.invoice || {}) }}
          onSuccess={onCreateSuccess}
          isInvoice
          isLeadSpecific={props.isLeadSpecific}
        />
      )}
      {Boolean(showPaymentModal) && (
        <IntuitPaymentModal
          isFromFirm
          handleClose={() => setShowPaymentModal(null)}
          paymentDetails={showPaymentModal}
          loadData={onCreateSuccess}
        />
      )}

      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Button
              label="Create Invoice"
              icon="pi pi-plus"
              className="p-button p-button-primary ms-auto"
              onClick={() => {
                setSelectedInvoice();
                setViewInvoice(false);
                setShowCreateInvoice(true);
              }}
            />
          </div>
        </div>
      </div>
      <InvoicesFiltersV2
        onFiltersChange={handleFiltersChange}
        isFromCaseSpecificModule={isFromCaseSpecificModule}
        isLeadSpecific={props.isLeadSpecific}
      />
      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={invoices || []}
          className="p-datatable-responsive"
          emptyMessage={isLoading ? 'Loading...' : 'No records found'}
          paginator={false}
          rows={pageLimit}
          storageKey="billing-invoices-list"
          sortMode="server"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          removableSort={false}
          columns={[
            { field: 'invoice_number', header: 'ID', body: tableCell, bodyClassName: 'ellipsis-text', sortable: true },
            {
              field: 'case_description.case_name',
              header: 'Case',
              body: caseBody,
              bodyClassName: 'ellipsis-text',
              hidden: !isFromGlobalBilling,
              sortable: isFromGlobalBilling,
            },
            { field: 'viewed_status', header: 'Viewed', body: viewBody, bodyClassName: 'ellipsis-text', sortable: true },
            {
              field: 'total_flat_fee',
              header: 'Total Fee',
              body: totalFeeBody,
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'invoice_status',
              header: 'Status',
              body: statusBody,
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            {
              field: 'due_amount',
              header: 'Due',
              body: dueBody,
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
            { field: 'plan_due_amount', header: 'Due Today', body: dueTodayBody, bodyClassName: 'ellipsis-text', sortable: true },
            { field: 'next_due_date', header: 'Next Due Date', body: nextDueDateBody, bodyClassName: 'ellipsis-text', sortable: true },
            { field: 'invoice_date', header: 'Sent Date', body: dueDateBody, bodyClassName: 'ellipsis-text', sortable: true },
            { field: 'due_date', header: 'Days Past Due', body: daysPastDueBody, bodyClassName: 'ellipsis-text', sortable: true },
            { field: 'action', header: '', body: actionBody, sortable: false, fixed: true, width: '180px', resizable: false },
          ]}
        />
        {/* External pagination using EllipsisPagination */}
        <EllipsisPagination
          currentPage={apiCurrentPage || currentPage}
          totalRecords={totalRecords}
          pageSize={pageLimit}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeDropdown={true}
        />
      </div>
    </div>
  );
};

export default InvoiceV2;
